import {isValidAmericanFormattedDate} from 'core/utils/date-helpers'
import {includes} from 'lodash'

/* tslint:disable:no-any */

// TODO I18N

////////////////
// Interfaces //
////////////////

export type Rule<D> = (value: any, allValues: D) => string | undefined

/////////////
// Helpers //
/////////////

function isEmpty(value: any): boolean {
  return !value || value === ''
}

export function joinRules<D>(rules: Array<Rule<D>>): Rule<D> {
  return (value, data) =>
    rules
      .map((rule) => rule(value, data))
      .filter((error) => !!error)
      [0] // First error
}

interface IData {
  [key: string]: any
}

export function createValidator<D extends IData>(
 rules: {[fieldKey: string]: Rule<D> | Array<Rule<D>>},
): (data: D) => {[fieldKey: string]: string} {
  return (data: D): {[fieldKey: string]: string} => {
    return Object.keys(rules).reduce((errors: {}, key: string) => {
      const rule = joinRules(([] as Array<Rule<D>>).concat(rules[key]))
      const error = rule(data[key], data)
      return error ? Object.assign(errors, {[key]: error}) : errors
    }, {}) // tslint:disable-line:align
  }
}

///////////
// Rules //
///////////

export const email: Rule<any> = (value: any) => {
  if (!isEmpty(value) && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\..{2,20}$/i.test(value)) {
    return 'Invalid email address'
  }
}

export const required: Rule<any> = (value: any) => {
  if (isEmpty(value)) {
    return 'Required'
  }
}

export function minLength(min: number): Rule<any> {
  return (value: any) => {
    if (!isEmpty(value) && value.length && value.length < min) {
      return `Must be at least ${min} characters`
    }
  }
}

export function npiValidator(length: number): Rule<any> {
  return (value: any) => {
    if (!isEmpty(value) && value.length && value.length !== length) {
      return `Must be ${length} digits`
    }
  }
}

export function minSize<T>(min: number): Rule<any> {
  return (value: T[]) => {
    if (value.length < min) {
      return `Must have at least ${min} items`
    }
  }
}

export function maxLength(max: number): Rule<any> {
  return (value: any) => {
    if (!isEmpty(value) && value.length && value.length > max) {
      return `Must be no more than ${max} characters`
    }
  }
}

export const integer: Rule<any> = (value) => {
  if (!Number.isInteger(Number(value))) {
    return 'Must be an integer'
  }
}

export function min(min: number, inclusive: boolean = true): Rule<any> {
  return (value: any) => {
    if (inclusive ? Number(value) < min : Number(value) <= min) {
      return `Must be ${inclusive ? 'at least' : 'greater than'} ${min}`
    }
  }
}

export function max(max: number, inclusive: boolean = true): Rule<any> {
  return (value: any) => {
    if (inclusive ? Number(value) > max : Number(value) >= max) {
      return `Must be ${inclusive ? 'at most' : 'less than'} ${max}`
    }
  }
}

/*
 * Requires the input to equal an element within a given set of acceptable values
 */
export function oneOf<T>(enumeration: T[]): Rule<any> {
  return (value: any) => {
    if (!includes(enumeration, value)) {
      return `Must be one of: ${enumeration.join(', ')}`
    }
  }
}

/*
 * Requires the input to exactly match the value of another field
 */
export function match<FormFields>(otherFieldKey: keyof FormFields, otherFieldName: string): Rule<any> {
  return (value: any, data?: {[key: string]: any}) => {
    if (data) {
      if (value !== data[otherFieldKey]) {
        return `Does not match ${otherFieldName}`
      }
    }
  }
}

export function regex(pattern: RegExp, example: string): Rule<any> {
  return (value: any) => {
    if (!pattern.test(value)) {
      return `Does not match the form ${example}`
    }
  }
}

/**
 * Requires the input to be date-formatted as MM/DD/YYYY string.
 */
export const americanFormattedDate: Rule<any> = (value: any) => {
  if (!isValidAmericanFormattedDate(value)) {
    return `Does not match the form MM/DD/YYYY`
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./core/src/validation/index.ts